import './MobilePriceFilterView2.css';

import React, { useCallback, useState, useMemo } from 'react';
import { Checkbox, Tabs, Typography, Modal, Button } from '@vtblife/uikit';
import { Size } from '@vtblife/uikit/legacy';
import { AreaUnit } from '@search/filter-enums/enums';

import classname from '@search/classname/src';

import { formatRange } from '@search/helpers/src/formatNumber';
import { ApplicationIdEnum } from '@search/graphql-typings';
import { MortgageStupidModal } from '../../../../../common/components/MinimalBanksMortgage/stupid/MortgageStupidModal';
import { PriceFilter, MortgagePaymentFilter } from '../../../../models/Filter';
import { NumberInputRange } from '../../../common/NewNumberInputRange/NumberInputRange';
import { MobileFilter2 } from '../../../filter/MobileFilter2';
import { CheckboxGroup2 } from '../../../common/CheckboxGroup2';
import { MortgagePaymentInput } from '../../desktop/price/DesktopPriceFilterView';
import { INumberRange } from '../../../../types/IRange';
import { priceByMortgagePayment } from '../../../../helpers/priceHelper';
import { MINIMAL_MORTGAGE_PAYMENT } from '../../../../helpers/contants';

const cn = classname.bind(null, 'MobilePriceFilterView2');

interface IMobilePriceFilterViewProps {
    filter: PriceFilter;
    priceTypes?: AreaUnit[];
    handleChange: (filter: PriceFilter | MortgagePaymentFilter) => void;
    updateFewFilters: (filters: (PriceFilter | MortgagePaymentFilter)[]) => void;
    mortgagePaymentFilter?: MortgagePaymentFilter;
}

const selectOptions = [ {
    value: AreaUnit.UNKNOWN,
    label: 'За всё'
}, {
    value: AreaUnit.ARE,
    label: 'За сотку'
}, {
    value: AreaUnit.METER,
    label: 'За м²'
} ];

enum TabsVariant {
    PRICE,
    PAYMENT
}

export const MobilePriceFilterView2: React.FC<IMobilePriceFilterViewProps> = ({
    filter,
    priceTypes = [ AreaUnit.UNKNOWN, AreaUnit.METER ],
    handleChange,
    updateFewFilters,
    mortgagePaymentFilter
}) => {
    const onChange = useCallback((value: INumberRange) => {
        if (mortgagePaymentFilter) {
            handleChange(new MortgagePaymentFilter());
        }
        handleChange(new PriceFilter(filter.priceType, value));
    }, [ filter, handleChange ]);
    const [ tabs, setTabs ] = useState<TabsVariant>(mortgagePaymentFilter?.payment ? TabsVariant.PAYMENT : TabsVariant.PRICE);
    const [ modal, setModal ] = useState<boolean>(false);
    const [ mortgageModal, setMortgageModal ] = useState(false);

    const closeModal = useCallback(() => {
        setModal(false);
        setMortgageModal(true);
    }, [ ]);
    const closeMortgageModal = useCallback(() => setMortgageModal(false), [ ]);

    const onChangePriceType = useCallback((value: AreaUnit) => {
        if (value === AreaUnit.MORTGAGE_PAYMENT) {
            handleChange(new PriceFilter(value, { from: null, to: null }));
            return;
        }
        handleChange(new PriceFilter(Number(value), filter.value));
    }, [ filter, handleChange, mortgagePaymentFilter ]);

    const filteredOptions = selectOptions.filter(item => {
        return priceTypes.includes(item.value);
    });

    const changeTab = useCallback((value: TabsVariant) => {
        setTabs(value);
        if (value === TabsVariant.PAYMENT) return;

        onChangePriceType(AreaUnit.UNKNOWN);
    }, [ onChangePriceType ]);

    const handleChangeMortgagePayment = useCallback((newFilter: MortgagePaymentFilter | PriceFilter) => {
        if (newFilter instanceof MortgagePaymentFilter && newFilter.payment) {
            updateFewFilters([ new PriceFilter(filter.priceType, { from: null, to: null }), newFilter ]);
        } else {
            handleChange(newFilter);
        }
    }, [ filter.priceType, handleChange, updateFewFilters ]);

    const mortgageFilterBlock = mortgagePaymentFilter ? (<MobileFilter2 filterTitle='Комфортный ежемесячный платёж'>
        <MortgagePaymentInput
            onChange={handleChangeMortgagePayment}
            filter={mortgagePaymentFilter}
            handlePriceType={onChangePriceType}
            mobile
        />
    </MobileFilter2>) : null;

    const priceFilterBlock = (<MobileFilter2 filterTitle='Цена'>
        <NumberInputRange
            size={Size.Small}
            unit='₽'
            fromPostfix='₽'
            value={filter.value}
            onChange={onChange}
            nameFrom='priceFrom'
            nameTo='priceTo'
        />
        <CheckboxGroup2
            mode='radio'
            // @ts-ignore
            onChange={onChangePriceType}
            name='price'
            value={[ String(filter.priceType) ]}
            withoutGrid
            classNameGroup={cn('group-price-types')}
            isFilter
        >
            {filteredOptions.map(({ value, label }) => (
                <Checkbox
                    variant='filter'
                    key={value}
                    name={String(value)}
                    block
                >
                    {label}
                </Checkbox>
            ))}
        </CheckboxGroup2>
    </MobileFilter2>);

    const priceFilter = (
        <Tabs justified onChange={changeTab} size='s' variant='badge'>
            <Tabs.Option active={tabs === TabsVariant.PRICE} value={0}>
                Цена жилья
            </Tabs.Option>
            <Tabs.Option active={tabs === TabsVariant.PAYMENT} value={1}>
                <div className={cn('tab')}>
                    <span>Платёж по ипотеке</span>
                    <span className={cn('circle')}>&#9679;</span>
                </div>
            </Tabs.Option>
        </Tabs>
    );

    const getCurrentFilter = (tab: TabsVariant) => {
        switch (tab) {
            case TabsVariant.PRICE:
                return priceFilterBlock;
            case TabsVariant.PAYMENT:
                return mortgageFilterBlock;
            default:
                return priceFilterBlock;
        }
    };

    const getTitle = useMemo(() => {
        return mortgagePaymentFilter?.payment ? `С таким платежом вы сможете купить квартиру ${formatRange(null, priceByMortgagePayment(mortgagePaymentFilter?.payment)).replace(/ /ig, '\u00A0')}.\u00A0` : null;
    }, [ mortgagePaymentFilter ]);

    return mortgagePaymentFilter ? (<>
        {priceFilter}
        {getCurrentFilter(tabs)}
        {tabs === TabsVariant.PAYMENT && (mortgagePaymentFilter?.payment ?? 0) >= MINIMAL_MORTGAGE_PAYMENT ? (
            <Typography variant='secondary-alone' className={cn('priceInfo')} onClick={() => setModal(true)}>
                {getTitle}
                <span style={{ color: '#3216B5' }}>Расчёт →</span>
            </Typography>
        ) : null}
        <Modal opened={modal} onClose={() => setModal(false)}>
            <Typography variant='primary' className={cn('modalInfo')}>
                <span style={{ fontWeight: 'bold' }}>
                    Это ориентировочный расчет.&nbsp;
                </span>
                Чтобы подобрать актуальные объявления мы&nbsp;используем медианное значение ипотечной ставки, фиксированный срок&nbsp;&mdash; 30&nbsp;лет и&nbsp;первоначальный взнос&nbsp;&mdash; 20%. На&nbsp;основе этих данных прогнозируем сумму кредита, которую вам могут одобрить банки и&nbsp;показываем квартиры подходящие под эти параметры.
                <br />
                <br />
                Чтобы узнать точную ставку, которую вам одобрят банки и&nbsp;получить дополнительную скидку&nbsp;&mdash; оставьте заявку.
            </Typography>
            <Modal.StickyActions>
                <Button
                    variant='accent'
                    fullWidth
                    onClick={closeModal}
                >
                    Узнать свою ставку по ипотеке
                </Button>
            </Modal.StickyActions>
        </Modal>
        {mortgageModal ? <MortgageStupidModal
            isMobile
            isNewBuilding={false}
            locationPlacement='промо-баннер'
            applicationId={ApplicationIdEnum.ClassifiedTouch}
            realtyType={null}
            propertyCost={priceByMortgagePayment(mortgagePaymentFilter.payment ?? 70000)}
            onClose={closeMortgageModal}
            rate={0.2}
        /> : null}
    </>) : priceFilterBlock;
};
